/**
 * 上课音频会话（前端侧）。
 *
 * 手机 App 里进教室要向原生申请一个音频会话，它管三件事：切后台不掐音频、
 * 走系统通话模式（回声消除更好）、来电被打断后能恢复。原生只报「发生了什么」，
 * 该不该静音由这里决定 —— 打断期间要把本地音轨停掉，否则电话那头能听见课堂声音。
 *
 * 网页版与桌面端：supported 恒为 false，所有函数是空转，调用方不必判平台。
 */

import { isDesktop } from './desktop'

export interface AudioSessionState {
  active: boolean
  interrupted: boolean
  route: string
  supported: boolean
}

const OFF: AudioSessionState = { active: false, interrupted: false, route: '', supported: false }

async function call(cmd: 'start' | 'stop' | 'state', args?: Record<string, unknown>): Promise<AudioSessionState> {
  if (!isDesktop()) return OFF
  try {
    const r = await window.__TAURI__!.core.invoke<AudioSessionState>(`plugin:audio-session|${cmd}`, args)
    return r ?? OFF
  } catch (e) {
    // 插件不在（旧版客户端）或系统拒绝：功能降级，但课不能因此进不去。
    console.warn('[音频会话]', cmd, e)
    return OFF
  }
}

/** 进教室后开启。title 用房间名，会显示在 Android 的通知栏上。 */
export function startAudioSession(title: string, text = '正在上课'): Promise<AudioSessionState> {
  return call('start', { args: { title, text } })
}

export function stopAudioSession(): Promise<AudioSessionState> {
  return call('stop')
}

export function audioSessionState(): Promise<AudioSessionState> {
  return call('state')
}

/**
 * 打断状态变化时该怎么处理本地麦克风（纯函数，便于测试）。
 *
 * `wasMuted` 是打断开始前用户自己的静音状态。恢复时必须还原成它，
 * 而不是一律取消静音 —— 用户本来可能就是静音的，挂断电话后被莫名打开麦克风
 * 是最糟的那种 bug（他以为自己没在说话）。
 */
export function onInterruptionChange(
  prev: boolean,
  next: boolean,
  currentlyMuted: boolean,
  wasMuted: boolean | null,
): { muted: boolean; remember: boolean | null } | null {
  if (prev === next) return null
  if (next) {
    // 打断开始：记下原状态并静音。
    return { muted: true, remember: currentlyMuted }
  }
  // 打断结束：还原。没记住过（应用刚启动就在打断中）按不静音处理。
  return { muted: wasMuted ?? false, remember: null }
}
